import { useMemo } from 'react';
import { posthog } from '@/lib/posthog';
import { useSharedModelView } from '@/hooks/use-public-deal';
import { useViewerOverrides } from '@/hooks/use-viewer-overrides';
import { useForkShare } from '@/hooks/use-shares';
import { ApiError } from '@/lib/api';
import type { ModelViewResponse } from '@/lib/api';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  ModelLayout,
  LayoutUnavailablePanel,
} from '@/components/builder/model-view';
import type { NarrativeBarShareMode } from '@/components/layout/NarrativeBar';
import { SandboxBanner } from './SandboxBanner';
import { authUrlWithShare } from './auth-share-url';

export interface DealModelViewProps {
  shareId: string;
  /**
   * Resolved viewer mode from the parent route's `resolveShareMode` —
   * drives the per-mode `<SandboxBanner>` copy + button. Undefined is
   * treated as `'external-anon'` (Phase 6 default behavior).
   */
  shareMode?: NarrativeBarShareMode;
  /**
   * admin-deal-oversight Phase 5: when true (the public-deal admin observer),
   * the sandbox-recompute edit path is suppressed (SandboxBanner + transientError
   * hidden, onInputEdit omitted). The useViewerOverrides hook STILL runs
   * unconditionally (rules-of-hooks; research/phase-5.md § 11 risk #3).
   */
  adminView?: boolean;
}

/**
 * `external-sharing` Phase 5 Session 5.3 (2026-05-09); editable-overlay
 * confirmed wired + rate-limit raised, `deal-signal-overhaul` Phase 6
 * Session 5 (2026-05-12).
 *
 * Public Model-tab body for `/deal/:shareId?tab=model`. Wraps
 * `<ModelLayout>` (the same auth-mode component shipped by
 * `in-platform-model-view` Phase 3) with public-deal data sourcing
 * via `useSharedModelView(shareId)` — Phase 4 Session 4.2's hook
 * over `GET /api/shared/:shareId/model-view`.
 *
 * Renders four states:
 *   1. Loading  → `<Skeleton variant="block">` placeholder.
 *   2. Error    → `<EmptyState title="MODEL UNAVAILABLE">` with
 *      friendly copy. 404 → "share revoked or invalid"; other 4xx/5xx
 *      → "couldn't load this model".
 *   3. Layout-missing → `<LayoutUnavailablePanel>` with
 *      `onCreateNew={undefined}` (public viewers can't create new
 *      conversations — the panel hides its CTA when the callback is
 *      absent, falling back to the contact-admin copy branch).
 *   4. Happy path → `<ModelLayout data={mergedData} conversationId={null}
 *      onInputEdit={sandbox.applyEdit} />`. **Input cells ARE editable** —
 *      `external-sharing` Phase 5 Session 5.4 wired the sandbox edit
 *      pathway: clicking an input cell opens `<CellDetailPanel>`'s
 *      `<InputBranch>` → `<InputCellEditor>` → commit fires `onInputEdit`
 *      → `useSandboxOverrides.applyEdit` → debounced
 *      `POST /api/shared/:shareId/sandbox-recompute` (no `conversationId`,
 *      no DB write, no Realtime echo — the consumer-side overlay merge
 *      below re-renders the grid). The `readOnly` prop on `<ModelGrid>` is
 *      vestigial (declared, unused in the body — all editing flows through
 *      `<CellDetailPanel>`, which has no `readOnly` gate); it is NOT passed
 *      here so as not to imply edits are blocked.
 *
 * **Edit-count policy (Phase 6 Session 5):** a viewer can make UNLIMITED
 * input edits — the 3-question cap + the 10/min `publicShareChatRateLimit`
 * apply ONLY to chat questions. The `sandboxRateLimit` (raised 60→600/min in
 * Phase 6 Session 5) is purely a DoS backstop on the per-recompute engine
 * cost; the `<InputCellEditor>`'s 250ms commit debounce already caps a human
 * editor well below it.
 */
export function DealModelView({
  shareId,
  shareMode = 'external-anon',
  adminView = false,
}: DealModelViewProps) {
  const query = useSharedModelView(shareId);
  const sandbox = useViewerOverrides(shareId); // UNCONDITIONAL — never gate this (§ 11 risk #3)
  const forkMutation = useForkShare();

  const hasOverrides = Object.keys(sandbox.overrides).length > 0;

  // Loading
  if (query.isLoading) {
    return (
      <div className="p-6 flex-1" data-testid="deal-model-view-loading">
        <Skeleton variant="block" className="h-96" />
      </div>
    );
  }

  // Error — distinguish 404 (share gone) from other errors for clearer copy.
  if (query.error) {
    const status = query.error instanceof ApiError ? query.error.status : null;
    const description =
      status === 404
        ? 'This share is no longer available. The deal owner may have revoked access.'
        : "Couldn't load this model. Please refresh the page or try again later.";
    return (
      <div
        className="flex-1 flex items-center justify-center p-6"
        data-testid="deal-model-view-error"
      >
        <EmptyState title="MODEL UNAVAILABLE" description={description} />
      </div>
    );
  }

  const data = query.data;

  // Race-condition fallback: query settled with no data and no error
  // (rare; happens if the request was cancelled mid-flight by a tab
  // change). Surface the same loading skeleton rather than blank.
  if (!data) {
    return (
      <div className="p-6 flex-1" data-testid="deal-model-view-loading">
        <Skeleton variant="block" className="h-96" />
      </div>
    );
  }

  // Layout-missing branch — model row predates `in-platform-model-view`
  // Phase 1's layout artifact. Public viewers can't fork into a new
  // conversation from here, so omit `onCreateNew` — the panel falls
  // through to the contact-admin copy branch.
  if (data.layoutMissing) {
    return (
      <div
        className="flex-1 flex items-center justify-center p-6"
        data-testid="deal-model-view-layout-missing"
      >
        <LayoutUnavailablePanel
          modelId={data.modelId}
          modelVersion={data.modelVersion}
          latestVersionWithLayout={data.latestVersionWithLayout}
        />
      </div>
    );
  }

  // Happy path — render the full Model layout with the sandbox-overlay
  // applied. Input cells are editable (commits route through
  // `sandbox.applyEdit` → `POST /api/shared/:shareId/sandbox-recompute`);
  // formula/output cells stay read-only as always (no editor for those).
  // Consumer-side merge keeps `<ModelLayout>` agnostic of sandbox state: we
  // synthesize a new `data` object with
  // `cellValues = { ...data.cellValues, ...sandbox.recomputedValues }` so
  // dependent panels (metrics card, sparklines via the <ModelLayout>'s
  // downstream tree) reflect the recomputed values after each edit.
  return (
    <DealModelViewHappyPath
      data={data}
      shareId={shareId}
      shareMode={shareMode}
      adminView={adminView}
      hasOverrides={hasOverrides}
      sandbox={sandbox}
      onSignupClick={() => {
        // Carry the shareId through to `/signup` (full-page nav, matching the
        // header CTAs' `<a href>` pattern) so post-auth `linkSharesForUser`
        // fires — a bare `navigate({to:'/signup'})` dropped it. 2026-06-04.
        posthog.capture('deal_signup_cta_clicked', {
          source: 'model-sandbox',
          share_id: shareId,
        });
        window.location.assign(authUrlWithShare('/signup', shareId));
      }}
      onForkClick={() => {
        forkMutation.mutate(shareId);
      }}
      onResetClick={() => {
        sandbox.clearOverrides();
      }}
    />
  );
}

interface DealModelViewHappyPathProps {
  data: ModelViewResponse;
  shareId: string;
  shareMode: NarrativeBarShareMode;
  adminView: boolean;
  hasOverrides: boolean;
  sandbox: ReturnType<typeof useViewerOverrides>;
  onSignupClick: () => void;
  onForkClick: () => void;
  onResetClick: () => void;
}

function DealModelViewHappyPath({
  data,
  shareId,
  shareMode,
  adminView,
  hasOverrides,
  sandbox,
  onSignupClick,
  onForkClick,
  onResetClick,
}: DealModelViewHappyPathProps) {
  // Synthesize the overlay-merged data once per render. `useMemo` keeps
  // <ModelLayout>'s `data` reference stable when neither inputs nor
  // recomputedValues change, avoiding a cascade of reconciles inside
  // the heavy grid renderer.
  //
  // Two merges, both required:
  //   - `cellValues` ← `sandbox.recomputedValues` (the full getValueAt
  //     sheet-walk the server returns post-override) → formula/output cells
  //     in the grid re-render with the recomputed values. `grid-helpers.ts
  //     resolveValue` reads `cellValues[${sheet}!${A1}]` for those.
  //   - `inputValues` ← `sandbox.overrides` (the `namedRange → value` map of
  //     applied edits) → the cell the USER TYPED INTO re-renders with the
  //     new value. `resolveValue` reads `inputValues[cell.namedRange]`
  //     DIRECTLY for `cellType === 'input'` cells — NOT `cellValues` — so
  //     without this second merge the edited input cell stays pinned to the
  //     frozen value forever even though everything downstream recomputed
  //     (the bug `deal-signal-overhaul` Phase 6 Session 5 follow-up fixed,
  //     2026-05-12). Override values are the canonical form already sent to
  //     the API (e.g. a percentage input commits 0.09, not 9) so merging
  //     into `inputValues` (also canonical) keeps `formatCellValue` happy.
  const mergedData = useMemo<ModelViewResponse>(
    () => ({
      ...data,
      cellValues: { ...data.cellValues, ...sandbox.recomputedValues },
      inputValues: { ...data.inputValues, ...sandbox.overrides },
    }),
    [data, sandbox.recomputedValues, sandbox.overrides]
  );

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden"
      data-testid="deal-model-view"
      data-share-id={shareId}
    >
      {!adminView && (
        <SandboxBanner
          shareMode={shareMode}
          hasOverrides={hasOverrides}
          onSignupClick={onSignupClick}
          onForkClick={onForkClick}
          onResetClick={onResetClick}
          className="mx-5 mt-3"
        />
      )}
      {!adminView && sandbox.transientError && (
        <div
          data-testid="sandbox-transient-error"
          role="alert"
          className="mx-5 mt-2 px-3 py-1.5 text-xs font-mono text-warn bg-warn-dim border border-warn/15 rounded-[2px]"
        >
          {sandbox.transientError}
        </div>
      )}
      <ModelLayout
        data={mergedData}
        conversationId={null}
        onInputEdit={adminView ? undefined : sandbox.applyEdit}
      />
    </div>
  );
}
